/**
 * PF Revamp — localStorage-backed DraftStore implementation.
 *
 * Storage layout:
 *   pf-drafts:index            — JSON `DraftMeta[]` (indexes all drafts)
 *   pf-drafts:<draftId>        — JSON `OrderDraftDTO` (full body)
 *
 * Design notes:
 * - Single-writer assumption (same tab). Multi-tab sync is a v2 concern
 *   (TODO(v2): use `storage` event + mutex). Two tabs writing to the same
 *   draftId concurrently will last-write-wins without warning.
 * - `create()` uses `crypto.randomUUID()` with a tiny fallback for envs
 *   where it's missing (Node < 19 / non-secure contexts). The fallback is
 *   NOT cryptographically secure — acceptable for a client-side draft id
 *   that the BE will eventually reassign.
 * - `QuotaExceededError` is caught, logged, and re-thrown so the caller
 *   (React Query mutation) surfaces it as an error state. We never silently
 *   drop a save.
 * - **W-22 ownership.** localStorage is device-wide, so drafts are scoped by an
 *   `ownerUserId` carried *in the data* (body + index row), not by the key —
 *   the key shape stays `pf-drafts:<draftId>` so nothing existing breaks.
 *   `save`/`create` stamp it from the id the caller threads in; `list` filters
 *   with `isDraftVisibleTo`. This module deliberately imports no auth/context:
 *   the signed-in id is resolved in `dataQueries/purchaseFlowDrafts.query.ts`.
 */

import type { OrderDraftDTO } from 'models/purchaseFlowRevamp'
import { logger } from 'utils/logging'

import { DRAFT_RETENTION_DAYS, deriveDraftMeta, isDraftVisibleTo, type DraftMeta, type DraftScope, type DraftStore } from './draftStore'
import { CURRENT_SCHEMA_VERSION, PF_VERSION, PF_VERSION_MIGRATIONS } from './pfVersion'

const INDEX_KEY = 'pf-drafts:index'
const DRAFT_KEY_PREFIX = 'pf-drafts:'

const draftKey = (draftId: string) => `${DRAFT_KEY_PREFIX}${draftId}`

/** UUID v4 generator with a non-secure hand-rolled fallback. */
function newDraftId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // RFC 4122 §4.4 layout, Math.random bits — good enough for a local id.
  const hex = (n: number) => Math.floor(Math.random() * n).toString(16)
  const seg = (len: number) => Array.from({ length: len }, () => hex(16)).join('')
  return `${seg(8)}-${seg(4)}-4${seg(3)}-${(8 + Math.floor(Math.random() * 4)).toString(16)}${seg(3)}-${seg(12)}`
}

/** Blank DTO used as the initial body of a freshly `create()`d draft. */
function emptyDraftDTO(draftId: string, scope: DraftScope = {}): OrderDraftDTO {
  const now = new Date().toISOString()
  return {
    schemaVersion: 1,
    pfVersion: PF_VERSION,
    draftId,
    createdAt: now,
    updatedAt: now,
    initiatedAs: 'CLIENT',
    ownerUserId: scope.ownerUserId,
    ownerWorkspaceId: scope.workspaceId,
    items: [],
    configs: {},
    instructions: {
      extraServices: [],
    },
    billing: {},
  }
}

function readIndex(storage: Storage): DraftMeta[] {
  const raw = storage.getItem(INDEX_KEY)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as DraftMeta[]) : []
  }
  catch (err) {
    logger.error('Failed to parse pf-drafts index', err, {
      context: { component: 'LocalDraftStore', action: 'readIndex' },
    })
    return []
  }
}

function writeIndex(storage: Storage, index: DraftMeta[]): void {
  storage.setItem(INDEX_KEY, JSON.stringify(index))
}

/**
 * Wipe every `pf-drafts:*` localStorage key. Used as a one-shot rollout
 * cleanup when draft semantics change. Idempotent — callers gate via a
 * separate sentinel key outside the `pf-drafts:` namespace so this
 * function can stay dumb. Returns the number of keys removed.
 */
export function purgeAllDrafts(storage: Storage = typeof window !== 'undefined' ? window.localStorage : ({} as Storage)): number {
  const toRemove: string[] = []

  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i)
    if (key && key.startsWith(DRAFT_KEY_PREFIX)) {
      toRemove.push(key)
    }
  }

  for (const key of toRemove) {
    storage.removeItem(key)
  }

  return toRemove.length
}

export class LocalDraftStore implements DraftStore {
  private readonly storage: Storage

  constructor(storage: Storage = typeof window !== 'undefined' ? window.localStorage : ({} as Storage)) {
    this.storage = storage
  }

  /**
   * Lists index rows for the current PF version that are visible to `scope`
   * (W-22 own-account + W-22b own-workspace; see `isDraftVisibleTo`). Bodies are
   * never read here. Drafts of any owner or version older than the retention
   * period are deleted first, since they hold personal data on this device.
   */
  async list(scope?: DraftScope): Promise<DraftMeta[]> {
    const index = this.deleteExpired(readIndex(this.storage))
    return index.filter((meta) => meta.pfVersion === PF_VERSION && isDraftVisibleTo(meta, scope))
  }

  private deleteExpired(index: DraftMeta[]): DraftMeta[] {
    const cutoff = Date.now() - DRAFT_RETENTION_DAYS * 24 * 60 * 60 * 1000
    const isExpired = (meta: DraftMeta) => (meta.updatedAt || meta.createdAt || 0) < cutoff
    const expired = index.filter(isExpired)
    if (expired.length === 0) return index

    for (const meta of expired) this.storage.removeItem(draftKey(meta.draftId))
    const kept = index.filter((meta) => !isExpired(meta))
    try {
      writeIndex(this.storage, kept)
    } catch (err) {
      logger.error('Failed to rewrite drafts index after deleting expired drafts', err, {
        context: { component: 'LocalDraftStore', action: 'deleteExpired' },
      })
    }
    return kept
  }

  async get(draftId: string): Promise<OrderDraftDTO | null> {
    const raw = this.storage.getItem(draftKey(draftId))
    if (!raw) return null
    try {
      const dto = JSON.parse(raw) as OrderDraftDTO
      // F-A8 — guard against drafts written by a NEWER client (higher schemaVersion)
      // that this client cannot safely hydrate (would silently drop extra properties).
      if (typeof dto.schemaVersion === 'number' && dto.schemaVersion > CURRENT_SCHEMA_VERSION) {
        logger.warn(
          'Dropped draft: schemaVersion too new for this client',
          { draftSchemaVersion: dto.schemaVersion, currentSchemaVersion: CURRENT_SCHEMA_VERSION },
          {
            context: {
              component: 'LocalDraftStore',
              action: 'get',
              metadata: { draftId, draftSchemaVersion: dto.schemaVersion },
            },
          }
        )
        return null
      }

      if (dto.pfVersion !== PF_VERSION) {
        // Attempt migration chain: walk from dto.pfVersion up to PF_VERSION - 1.
        // Each step must have an entry in PF_VERSION_MIGRATIONS; if any is
        // missing the chain is incomplete and the draft is unrecoverable.
        if (
          typeof dto.pfVersion === 'number' &&
          dto.pfVersion < PF_VERSION
        ) {
          let current: unknown = dto
          let fromVersion = dto.pfVersion
          let migratable = true

          while (fromVersion < PF_VERSION) {
            const migrate = PF_VERSION_MIGRATIONS[fromVersion]
            if (!migrate) {
              migratable = false
              break
            }
            current = migrate(current)
            fromVersion++
          }

          if (migratable) {
            logger.warn(
              'Migrated draft from older pfVersion',
              { fromVersion: dto.pfVersion, toVersion: PF_VERSION },
              {
                context: {
                  component: 'LocalDraftStore',
                  action: 'get',
                  metadata: { draftId, fromVersion: dto.pfVersion, toVersion: PF_VERSION },
                },
              }
            )
            // Shape-validate the migrated DTO before returning it.
            // Migrations are user-supplied functions and could produce malformed
            // output — run the same F-A25 guards as the non-migration path.
            const migrated = current as OrderDraftDTO
            if (!Array.isArray(migrated.items)) {
              logger.warn('Dropped migrated draft: items is not an array post-migration', undefined, {
                context: { component: 'LocalDraftStore', action: 'get', metadata: { draftId } },
              })
              return null
            }
            if (typeof migrated.billing !== 'object' || migrated.billing === null || Array.isArray(migrated.billing)) {
              logger.warn('Dropped migrated draft: billing is not an object post-migration', undefined, {
                context: { component: 'LocalDraftStore', action: 'get', metadata: { draftId } },
              })
              return null
            }
            return migrated
          }
        }

        logger.warn(
          'Dropped draft due to pfVersion mismatch (no migration path)',
          { draftVersion: dto.pfVersion, currentVersion: PF_VERSION },
          {
            context: {
              component: 'LocalDraftStore',
              action: 'get',
              metadata: { draftId, draftVersion: dto.pfVersion, currentVersion: PF_VERSION },
            },
          }
        )
        return null
      }

      // F-A25 — lightweight runtime shape guard. OrderDraftDTO arrives from
      // localStorage and may be corrupt (truncated write, manual edit, or a
      // pfVersion that passed the check above but has a different shape).
      // `hydrateFromDraft` reads `dto.items` and `deriveDraftMeta` iterates it —
      // a non-array would throw a runtime crash.
      if (!Array.isArray(dto.items)) {
        logger.warn('Dropped malformed draft: items is not an array', undefined, {
          context: { component: 'LocalDraftStore', action: 'get', metadata: { draftId } },
        })
        return null
      }
      if (typeof dto.billing !== 'object' || dto.billing === null || Array.isArray(dto.billing)) {
        logger.warn('Dropped malformed draft: billing is not an object', undefined, {
          context: { component: 'LocalDraftStore', action: 'get', metadata: { draftId } },
        })
        return null
      }

      return dto
    }
    catch (err) {
      logger.error('Failed to parse pf-draft body', err, {
        context: { component: 'LocalDraftStore', action: 'get', metadata: { draftId } },
      })
      return null
    }
  }

  async save(draftId: string, dto: OrderDraftDTO, scope: DraftScope = {}): Promise<void> {
    // Stamp updatedAt on every save — createdAt preserved from existing index
    // entry when possible, else from the incoming DTO.
    const index = readIndex(this.storage)
    const existing = index.find((d) => d.draftId === draftId)
    const now = new Date().toISOString()
    // W-22 — (re)stamp ownership on every save, which is what turns a resumed
    // legacy draft into an owned one. Fall back through body → existing index
    // row so a save made while `/user/me` is unresolved can never *downgrade*
    // an already-owned draft into a cross-visible unowned one.
    const owner = scope.ownerUserId ?? dto.ownerUserId ?? existing?.ownerUserId
    // W-22b — same fall-through on the workspace axis, for the same reason: a
    // save made before the workspace resolves must not widen an already-scoped
    // draft back into a cross-workspace one.
    const ownerWorkspace = scope.workspaceId ?? dto.ownerWorkspaceId ?? existing?.ownerWorkspaceId
    const stamped: OrderDraftDTO = {
      ...dto,
      draftId,
      updatedAt: now,
      ownerUserId: owner,
      ownerWorkspaceId: ownerWorkspace,
    }

    try {
      this.storage.setItem(draftKey(draftId), JSON.stringify(stamped))
    }
    catch (err) {
      logger.error('Failed to persist draft body (quota exceeded?)', err, {
        context: { component: 'LocalDraftStore', action: 'save', metadata: { draftId } },
      })
      throw err
    }

    const derived = deriveDraftMeta(stamped)
    const nextMeta: DraftMeta = {
      draftId,
      propertyAddress: derived.propertyAddress,
      itemCount: derived.itemCount,
      productNames: derived.productNames,
      sessionId: derived.sessionId,
      updatedAt: derived.updatedAt,
      createdAt: existing?.createdAt ?? derived.createdAt,
      pfVersion: derived.pfVersion,
      ownerUserId: derived.ownerUserId,
      ownerWorkspaceId: derived.ownerWorkspaceId,
    }

    const nextIndex = existing
      ? index.map((d) => (d.draftId === draftId ? nextMeta : d))
      : [...index, nextMeta]

    try {
      writeIndex(this.storage, nextIndex)
    }
    catch (err) {
      logger.error('Failed to persist drafts index (quota exceeded?)', err, {
        context: { component: 'LocalDraftStore', action: 'save', metadata: { draftId } },
      })
      throw err
    }
  }

  async create(scope?: DraftScope): Promise<string> {
    const draftId = newDraftId()
    // W-22 / W-22b — scoped from birth when the ids are known, so an
    // auto-created draft is never briefly listed for another account or
    // workspace.
    const dto = emptyDraftDTO(draftId, scope)

    try {
      this.storage.setItem(draftKey(draftId), JSON.stringify(dto))
    }
    catch (err) {
      logger.error('Failed to create draft body', err, {
        context: { component: 'LocalDraftStore', action: 'create', metadata: { draftId } },
      })
      throw err
    }

    const index = readIndex(this.storage)
    const derived = deriveDraftMeta(dto)
    const nextIndex: DraftMeta[] = [
      ...index,
      { draftId, ...derived },
    ]

    try {
      writeIndex(this.storage, nextIndex)
    }
    catch (err) {
      logger.error('Failed to update drafts index after create', err, {
        context: { component: 'LocalDraftStore', action: 'create', metadata: { draftId } },
      })
      // Roll back the orphaned body write so list() stays consistent.
      this.storage.removeItem(draftKey(draftId))
      throw err
    }

    return draftId
  }

  async delete(draftId: string): Promise<void> {
    this.storage.removeItem(draftKey(draftId))
    const index = readIndex(this.storage)
    const nextIndex = index.filter((d) => d.draftId !== draftId)
    if (nextIndex.length !== index.length) {
      writeIndex(this.storage, nextIndex)
    }
  }
}
